import { AxiosRequestConfig } from "axios";
import { ToastProgrammatic as Toast } from "buefy";
import Vue from "vue";

import i18n from "@/i18n";
import store from "@/store";

export default class BaseApiClient {
  public api = process.env.VUE_APP_API_URL;

  /* Internal Methods */
  get config(): AxiosRequestConfig {
    // If there is a JWT, use it in the request.
    try {
      return {
        headers: {
          Authorization: `Bearer ${store.state.auth.jwts.id_token}`,
          Accept: "application/json"
        }
      };
    } catch (e) {
      return {};
    }
  }

  protected post<T>(
    endpoint: string,
    payload: any,
    params?: any,
    ignoreStatusCodes: number[] = [],
    onUploadProgress?: (progressEvent: any) => void,
    contentType?: string
  ): Promise<T> {
    const config = this.config;
    if (params) {
      config["params"] = params;
    }

    if (onUploadProgress) {
      config["onUploadProgress"] = onUploadProgress;
    }

    if (contentType) {
      config.headers["Content-Type"] = contentType;
    }

    return new Promise<T>((resolve, reject) => {
      Vue.axios
        .post(`${this.api}${endpoint}`, payload, config)
        .then(result => {
          resolve(result.data);
        })
        .catch(error => {
          BaseApiClient.handleError(error, ignoreStatusCodes);
          reject(error);
        });
    });
  }

  protected get<T>(
    endpoint: string,
    params?: any,
    ignoreStatusCodes: number[] = []
  ): Promise<T> {
    const config = this.config;
    if (params) {
      config["params"] = params;
    }

    return new Promise<T>((resolve, reject) => {
      Vue.axios
        .get(`${this.api}${endpoint}`, config)
        .then(result => {
          resolve(result.data);
        })
        .catch(error => {
          BaseApiClient.handleError(error, ignoreStatusCodes);
          reject(error);
        });
    });
  }

  protected delete<T>(
    endpoint: string,
    params?: any,
    ignoreStatusCodes: number[] = []
  ): Promise<T> {
    const config = this.config;
    if (params) {
      config["params"] = params;
    }

    return new Promise<T>((resolve, reject) => {
      Vue.axios
        .delete(`${this.api}${endpoint}`, config)
        .then(result => {
          resolve(result.data);
        })
        .catch(error => {
          BaseApiClient.handleError(error, ignoreStatusCodes);
          reject(error);
        });
    });
  }

  private static handleError(error: any, ignoreStatusCodes: number[]) {
    if (error.response) {
      const status = error.response.status;
      if (ignoreStatusCodes.includes(status)) {
        return;
      }
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (status === 404) {
        Toast.open({
          type: "is-danger",
          message: i18n.t("api.notFound"),

          duration: 3000,
          queue: false
        });
      } else if (
        error.response.status === 401 ||
        error.response.status === 403
      ) {
        Toast.open({
          type: "is-danger",
          message: i18n.t("api.forbidden"),
          duration: 3000,
          queue: false
        });
      } else if (error.response.status >= 500) {
        Toast.open({
          type: "is-danger",
          message: i18n.t("api.serverError"),
          duration: 3000,
          queue: false
        });
      } else if (error.response.status >= 400) {
        Toast.open({
          type: "is-danger",
          message: i18n.t("api.wrongFormat"),
          duration: 3000,
          queue: false
        });
      } else {
        Toast.open({
          type: "is-danger",
          message: i18n.t("api.unknown"),
          duration: 3000,
          queue: false
        });
      }
    } else {
      Toast.open({
        type: "is-danger",
        message: i18n.t("api.unknown"),
        duration: 3000,
        queue: false
      });
    }
  }
}
